import React, { useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import SchematicCableView from '../visualization/SchematicCableView';
import ImportProjectModal from './ImportProjectModal';

const LandingPage = ({ onStartNew, onImport, isMobile = false }) => {
  const { t, language } = useI18n();
  const [hoveredCable, setHoveredCable] = useState(null);
  const [listSortMode, setListSortMode] = useState('numerazione');
  const [showImportModal, setShowImportModal] = useState(false);

  // Dati di esempio reali basati sul dataset utente e richieste
  const RAW_dummyCables = [
    // #1 Cavo Singolo
    { type: 'single', siglatura: '0', colore: 'ROSSO', sezione: '2.5', lunghezza: 750, terminaleA: 'PSB', terminaleB: 'OB6', collegamentoA: 'MORS. PE ▲', collegamentoB: 'BOX', originalIndex: 0 },

    // #2-5 Cavo Multiplo (Gruppo Siglatura 1)
    { type: 'single', siglatura: '1', colore: 'GI/VE', sezione: '0.5', lunghezza: 300, terminaleA: 'PSW', terminaleB: 'PDW', collegamentoA: 'MORS. PE ▲', collegamentoB: 'G2.8--V', originalIndex: 1 },
    { type: 'single', siglatura: '1', colore: 'BLU', sezione: '0.5', lunghezza: 400, terminaleA: 'PDW', terminaleB: 'PDW', collegamentoA: 'PLC-CN7 -', collegamentoB: 'G2.8--V', originalIndex: 2 },
    { type: 'single', siglatura: '1', colore: 'BLU', sezione: '0.5', lunghezza: 200, terminaleA: 'PDW', terminaleB: 'PDW', collegamentoA: 'PLC-CN7 -', collegamentoB: 'EXP-CN6 -', originalIndex: 3 },
    { type: 'single', siglatura: '1', colore: 'BLU', sezione: '0.5', lunghezza: 200, terminaleA: 'PSW', terminaleB: 'PDW', collegamentoA: 'MORS. 0 ▲', collegamentoB: 'EXP-CN6 -', originalIndex: 4 },

    // #6-12 Cavi in Guaina (Tra cui il Multipolare e vari singoli)
    { type: 'single', guaina: 1, colore: 'GI/VE', sezione: '2.5', lunghezza: 1800, terminaleA: 'OB6', terminaleB: 'PSB', collegamentoA: '', collegamentoB: 'MORS. PE ▲', originalIndex: 5 },
    { type: 'single', guaina: 1, colore: 'GI/VE', sezione: '1', lunghezza: 1800, terminaleA: 'NS', terminaleB: 'PSR', collegamentoA: '', collegamentoB: 'MORS. PE ▲', originalIndex: 6 },
    { type: 'single', guaina: 1, siglatura: '0', colore: 'BLU', sezione: '0.5', lunghezza: 1450, terminaleA: 'NS', terminaleB: 'PSW', collegamentoA: '', collegamentoB: 'MORS. 0 ▼', originalIndex: 7 },
    { type: 'single', guaina: 1, siglatura: '12', colore: 'BLU', sezione: '0.5', lunghezza: 1450, terminaleA: 'NS', terminaleB: 'PSW', collegamentoA: '', collegamentoB: 'F2.7 ▼', originalIndex: 8 },
    { type: 'single', guaina: 1, siglatura: '30', colore: 'ARANCIO', sezione: '0.5', lunghezza: 1450, terminaleA: 'PSW', terminaleB: 'PSW', collegamentoA: '', collegamentoB: 'MORS. 30 ▼', originalIndex: 9 },
    { type: 'single', guaina: 1, siglatura: '32', colore: 'VIOLA', sezione: '0.5', lunghezza: 1450, terminaleA: 'PSW', terminaleB: 'PSW', collegamentoA: '', collegamentoB: 'MORS. 32 ▼', originalIndex: 10 },

    // #11 Cavo Multipolare reale da immagine
    {
      type: 'multipolar',
      nomeCavo: 'Sensore RS485',
      siglatura: 'B5 2G0.5 SCHERM',
      tipoCavo: 'B5 2G0.5 SCHERM',
      lunghezza: 1400,
      guaina: 1,
      guainaEsterna: '6.5',
      terminaleA_multi: '-',
      terminaleB_multi: '-',
      sguainaturaA: 60,
      sguainaturaB: 50,
      collegamentoA: '',
      collegamentoB: 'RS485',
      poli: [
        { colore: 'MARRONE', sezione: '0.5', poloID: 'P1', siglaturaA: 'A+', siglaturaB: 'A+', terminaleA: 'SP5/2', terminaleB: 'PSW', lunghezzaA: 60, lunghezzaB: 50 },
        { colore: 'BIANCO', sezione: '0.5', poloID: 'P2', siglaturaA: 'B-', siglaturaB: 'B-', terminaleA: 'SP5/2', terminaleB: 'PSW', lunghezzaA: 60, lunghezzaB: 50 }
      ],
      originalIndex: 11
    }
  ];

  const dummyCables = RAW_dummyCables;

  const dummySheaths = [
    { id: 1, label: 'Guaina Trecciata PET 10', fullLabel: 'Guaina Trecciata PET 10', blockType: 'sheath' }
  ];

  const dummySettings = {
    lingua: language,
    marcaturePerCollegamento: false,
    useAWG: false
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col font-sans relative">
      {/* Elementi decorativi di sfondo allargati e modificati per risaltare maggiormente */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[120%] bg-blue-600 opacity-20 rounded-full blur-[100px] transform rotate-45"></div>
        <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[80%] bg-cyan-400 opacity-15 rounded-full blur-[120px]"></div>
      </div>

      <main className="flex-1 relative z-10 flex flex-col items-center justify-center px-3 sm:px-6 lg:px-8 mt-10 mb-16 md:mt-24 md:mb-20">

        {/* Intestazione Principale */}
        <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16 animate-fade-in-up">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-5 md:mb-6 tracking-tight drop-shadow-md">
            {t('landing.welcomeTitle')}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-8 font-light max-w-2xl mx-auto leading-relaxed">
            {t('landing.description')}
          </p>

          {/* Action Buttons: Colori più accesi e contrastanti */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-10">
            <button
              onClick={onStartNew}
              className="group relative px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:bg-blue-500 transition-all duration-300 transform hover:-translate-y-1 w-full sm:w-auto flex items-center justify-center gap-3 overflow-hidden border border-blue-400/30"
            >
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <Icons.Plus className="w-6 h-6 relative z-10" />
              <span className="relative z-10">{t('landing.createNew')}</span>
            </button>

            <button
              onClick={() => setShowImportModal(true)}
              className="group px-8 py-4 bg-white text-blue-900 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:bg-gray-50 border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 w-full sm:w-auto flex items-center justify-center gap-3"
            >
              <Icons.Upload className="w-6 h-6 text-blue-700 group-hover:text-blue-600 transition-colors" />
              <span className="relative z-10">{t('landing.importProject')}</span>
            </button>
          </div>
        </div>

        {showImportModal && (
          <ImportProjectModal
            onClose={() => setShowImportModal(false)}
            onImport={onImport}
            t={t}
          />
        )}

        {/* Simulazione / Mockup App */}
        <div className="w-full max-w-6xl mx-auto mt-4 mb-16 md:mb-24 relative shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200/50 bg-white md:transform md:hover:scale-[1.01] md:transition-transform md:duration-500">
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div className="mx-auto bg-white rounded-md px-4 sm:px-10 py-1 text-xs text-gray-500 font-medium shadow-sm border border-gray-200">
              WireCraft Preview
            </div>
          </div>
          <div className="p-2 sm:p-6 md:p-8 bg-white relative">
            {/* Resterizziamo lo Schematic fittizio con interazione reale abilitata */}
            <div className="relative">
              {/* Velo superiore rimovibile o abbassato di opacità per poter interagire */}
              <div className="absolute inset-0 pointer-events-none z-10 rounded-xl bg-gradient-to-t from-white/30 to-transparent"></div>
              <SchematicCableView
                cables={dummyCables}
                settings={{ ...dummySettings, listSortMode }}
                setListSortMode={setListSortMode}
                hoveredCable={hoveredCable}
                setHoveredCable={setHoveredCable}
                isMobile={isMobile}
                showDeleteButtons={new Set()}
                isSelectionModeEnabled={false}
                selectedCables={new Set()}
                availableSheaths={dummySheaths}
                validateCable={() => ({ isValid: true, missingFields: [] })}
                // Dummy handlers
                editCable={() => { }}
                editMultipolarCable={() => { }}
                deleteCable={() => { }}
                deleteMultipolarCable={() => { }}
                toggleDeleteButton={() => { }}
                editingIndex={-1}
                editingMultipolarIndex={-1}
                isPreviewMode={true}
                previewTitle={
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2 m-0">
                    <Icons.Eye className="w-5 h-5 text-blue-500" />
                    {t('landing.welcomeSubtitle')}
                  </h3>
                }
              />
            </div>
          </div>
        </div>

        {/* Sezione Features */}
        <div className="w-full max-w-6xl mx-auto py-12 border-t border-gray-200">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4 tracking-tight drop-shadow-sm">{t('landing.featuresTitle')}</h2>
            <div className="w-24 h-1 bg-blue-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4">

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                <Icons.Eye className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('landing.feature1Title')}</h3>
              <p className="text-gray-600 leading-relaxed font-medium">{t('landing.feature1Desc')}</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6 text-green-600">
                <Icons.Settings className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('landing.feature2Title')}</h3>
              <p className="text-gray-600 leading-relaxed font-medium">{t('landing.feature2Desc')}</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-6 text-purple-600">
                <Icons.Download className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('landing.feature3Title')}</h3>
              <p className="text-gray-600 leading-relaxed font-medium">{t('landing.feature3Desc')}</p>
            </div>

          </div>
        </div>

      </main>

      {/* Stili CSS iniettati per piccole animazioni se non presenti in Tailwind config */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}} />
    </div>
  );
};

export default LandingPage;
